import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import QuantityField from '../QuantityField';
import { Vaccination, VaccinationService, UpdateVaccinationDto } from '../../services/vaccinationService';
import { executeUpdate, OfflineWriteBlockedError } from '../../offline';

interface EditVaccinationDialogProps {
  vaccination: Vaccination | null;
  horseId: string;
  onClose: () => void;
}

/**
 * Correct a saved vaccination (spec §21): typically the lot number or expiry that was not to hand when it
 * was recorded. Stock follows the correction (ADR-0047 A3): a new lot gives the dose back to the old one and takes
 * it from the new one, and a new amount moves the difference.
 */
const EditVaccinationDialog: React.FC<EditVaccinationDialogProps> = ({ vaccination, horseId, onClose }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [lotNumber, setLotNumber] = useState('');
  const [lotExpiresOn, setLotExpiresOn] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [doseMl, setDoseMl] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vaccination) return;
    setLotNumber(vaccination.lotNumber ?? '');
    // A calendar day: its date part, never re-zoned.
    setLotExpiresOn(vaccination.lotExpiresOn ? vaccination.lotExpiresOn.slice(0, 10) : '');
    setManufacturer(vaccination.manufacturer ?? '');
    setDoseMl(vaccination.doseMl ?? null);
    setNotes(vaccination.notes ?? '');
    setError(null);
  }, [vaccination]);

  const handleSave = async () => {
    if (!vaccination) return;
    setSaving(true);
    setError(null);
    const payload: UpdateVaccinationDto = {
      lotNumber: lotNumber.trim() || null,
      lotExpiresOn: lotExpiresOn || null,
      manufacturer: manufacturer.trim() || null,
      doseMl: doseMl && doseMl > 0 ? doseMl : null,
      notes,
    };
    try {
      await executeUpdate({
        entityType: 'vaccination',
        table: 'vaccinations',
        entityId: vaccination.id,
        payload,
        networkFn: () => VaccinationService.update(vaccination.id, payload),
      });
      queryClient.invalidateQueries(['vaccinations', horseId]);
      queryClient.invalidateQueries(['horse-records', horseId]);
      enqueueSnackbar('Vaccination updated', { variant: 'success' });
      onClose();
    } catch (err: any) {
      if (err instanceof OfflineWriteBlockedError || err?.name === 'OfflineWriteBlockedError') {
        setError(err.message);
      } else {
        setError(err?.response?.data?.message || 'Failed to update the vaccination');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!vaccination} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {vaccination?.vaccineName ?? 'vaccination'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField label="Lot number" size="small" fullWidth value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Lot expiration"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={lotExpiresOn}
              onChange={(e) => setLotExpiresOn(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Manufacturer" size="small" fullWidth value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuantityField label="Amount given (mL)" size="small" fullWidth value={doseMl} onChange={setDoseMl} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes" size="small" fullWidth multiline value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
          Saving moves the stock to match: a new lot gives the dose back to the old lot and takes it from the new one.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditVaccinationDialog;
